import { AlignLeft, FileText, Paperclip, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { type NewSourceType, useCreateSource } from "@/hooks/use-sources";
import { cn } from "@/lib/utils";

type AddSourceMode = "paste" | "file";

interface ModeOption {
  id: AddSourceMode;
  label: string;
  sub: string;
  icon: typeof AlignLeft;
  apiType: NewSourceType;
}

const PASTE_MODE: ModeOption = {
  id: "paste",
  label: "Paste",
  sub: "Transcript, PRD, Slack thread, email, notes",
  icon: AlignLeft,
  apiType: "paste",
};

const FILE_MODE: ModeOption = {
  id: "file",
  label: "File",
  sub: "Upload a Markdown (.md) file",
  icon: FileText,
  apiType: "document",
};

const MODES: ReadonlyArray<ModeOption> = [PASTE_MODE, FILE_MODE];

const PASTE_PLACEHOLDER =
  "Paste anything — a meeting transcript, a PRD or audit memo, a Slack thread, an email, a survey export, or your own notes. The AI extracts requirement candidates from the text.";

interface AddSourceCardProps {
  solutionId: string;
  onAdded?: () => void;
}

export function AddSourceCard({ solutionId, onAdded }: AddSourceCardProps) {
  const create = useCreateSource(solutionId);

  const [modeId, setModeId] = useState<AddSourceMode>("paste");
  const [name, setName] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [rawText, setRawText] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const mode = MODES.find((m) => m.id === modeId) ?? PASTE_MODE;

  function reset() {
    setModeId("paste");
    setName("");
    setDate(new Date().toISOString().slice(0, 10));
    setRawText("");
    setFile(null);
  }

  async function submit() {
    if (!name.trim()) return;

    let body: string | null = rawText.trim() || null;
    if (modeId === "file") {
      if (!file) return;
      try {
        body = (await file.text()).trim() || null;
      } catch {
        toast.error("Failed to read file");
        return;
      }
    }

    create.mutate(
      {
        type: mode.apiType,
        name: name.trim(),
        sourceDate: new Date(`${date}T00:00:00Z`).toISOString(),
        rawText: body,
      },
      {
        onSuccess: () => {
          toast.success(`Source added — ${mode.label}`);
          reset();
          onAdded?.();
        },
      },
    );
  }

  const canSubmit =
    name.trim().length > 0 && !create.isPending && (modeId !== "file" || file !== null);

  return (
    <section className="rounded-xl border border-[var(--color-border)] bg-card p-5 space-y-4">
      <h2 className="font-display text-base font-semibold">Add a source</h2>

      <div className="grid grid-cols-2 gap-2.5">
        {MODES.map((m) => {
          const Icon = m.icon;
          const isActive = m.id === modeId;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setModeId(m.id)}
              className={cn(
                "rounded-xl border-2 px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm",
                isActive
                  ? "border-nymbl-navy bg-nymbl-sky/30"
                  : "border-[var(--color-border)] bg-card hover:border-nymbl-blue",
              )}
            >
              <Icon className="h-5 w-5 text-nymbl-navy mb-2" />
              <div className="font-display font-semibold text-sm leading-tight">{m.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{m.sub}</div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
        <div>
          <label
            htmlFor="add-source-name"
            className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
          >
            Source name
          </label>
          <Input
            id="add-source-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={
              modeId === "paste" ? "e.g. Discovery kickoff — Apr 17" : "e.g. Engineering audit memo"
            }
            className="h-9 text-sm"
          />
        </div>
        <div>
          <label
            htmlFor="add-source-date"
            className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
          >
            Date
          </label>
          <Input
            id="add-source-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {modeId === "paste" ? (
        <div>
          <Textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder={PASTE_PLACEHOLDER}
            rows={5}
            className="text-sm"
          />
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-[var(--color-border)] p-5 space-y-2">
          <Input
            type="file"
            accept=".md,.markdown,text/markdown"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              if (f && !name.trim()) {
                setName(f.name.replace(/\.(md|markdown)$/i, ""));
              }
            }}
            className="text-sm"
          />
          {file ? (
            <div className="text-xs text-muted-foreground">
              {file.name} — {(file.size / 1024).toFixed(1)} KB
            </div>
          ) : (
            <div className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Paperclip className="h-3.5 w-3.5" />
              Markdown (.md) only. Contents are read in the browser.
            </div>
          )}
          <div className="text-[11px] text-muted-foreground/80">More file formats coming soon.</div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="outline" onClick={reset} disabled={create.isPending}>
          Reset
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {create.isPending ? "Adding…" : "Add source"}
        </Button>
      </div>
    </section>
  );
}
